import { useLazyQuery, useMutation } from "@apollo/client";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import InputField from "../../../../components/layout/InputField";
import { VIEW_PLACEMENT_MANAGEMENT_ROUTE } from "../../../../utils/constant";
import { ENDED_PLACEMENT, } from "../gql";
import { GET_PLACEMENT_BY_ID } from "../../../AccountingDashboard/gql";
import CustomButton from "../../../../components/layout/CustomButton";


const MoldalForDirectHire = ({
  isCancellationRequest = false,
  endDateModal,
  setEndDateModal,
  placementId,
  refetch,
  AccountingCancel,
  history,
}) => {

  type FormValues = {
    placementJobTitle?: string;
    placementEndDate?: string;
    placementStartDate?: string;
    cancelReason?: string;
  };

  const { formState: { errors }, control, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      placementJobTitle: placementId?.jobTitle || "",
      placementEndDate: "",
      placementStartDate: "",
      cancelReason: "",
    },
    shouldUnregister: true,
  });
  const [endedPlacement, { loading: endingPlacementLoading }] = useMutation(ENDED_PLACEMENT);
  const [getPlacementById, { data: placementData, loading }] = useLazyQuery(GET_PLACEMENT_BY_ID);

  useEffect(() => {
    if (endDateModal && AccountingCancel && placementId) {
      getPlacementById({
        variables: {
          id: placementId?.id,
        },
      });
    }
  }, [endDateModal, AccountingCancel, placementId, getPlacementById]);

  useEffect(() => {
    if (endDateModal && AccountingCancel && !loading) {
      setValue("cancelReason", placementData?.getPlacementById?.statusNote || "");
    }
  }, [endDateModal, AccountingCancel, loading, placementData, setValue]);

  const reload = () => {
    setTimeout(() => {
      return window.location.reload();
    }, 1000);
  };

  const handleSave = async (value) => {
    const finalValues = {
      id: placementId?.id ? placementId.id : placementId,
      placementEndDate: value.placementEndDate,
      placementStartDate: value?.placementStartDate,
      AccountingCancel: AccountingCancel,
      cancelReason: value?.cancelReason,
      jobTitle: value?.placementJobTitle
    };
    const placementUpdateSaving = await endedPlacement({
      variables: {
        updatePlacementDTO: finalValues,
      },
    });
    if (placementUpdateSaving?.data?.endedPlacement?.status === 200) {
      !AccountingCancel
        ? toast.success("Placement ended successfully.")
        : toast.success("Placement cancelled successfully.")
      setEndDateModal(false);
      !AccountingCancel
        ? history.push({
          pathname: `${VIEW_PLACEMENT_MANAGEMENT_ROUTE}/${placementUpdateSaving?.data?.endedPlacement?.newPlacementID}`,
        })
        : refetch();
    } else if (placementUpdateSaving?.data?.endedPlacement?.status === 202) {
      setEndDateModal(false);
      toast.success("Placement cancellation request generated.");
      reload()
      refetch();
    } else if (placementUpdateSaving?.data?.endedPlacement?.status === 206) {
      setEndDateModal(false);
      toast.success("Placement cancelled successfully.");
      toast.success("Request status changed to approved.");
      refetch();
    } else {
      setEndDateModal(false);
      !AccountingCancel
        ? toast.warn("Placement did not end.")
        : toast.warn("Placement not cancelled.");
    }
  };

  return (
    <Modal
      isOpen={endDateModal}
      toggle={() => setEndDateModal(!endDateModal)}
      className="revampDialog"
    >
      <ModalHeader>
        {AccountingCancel && !isCancellationRequest ? (
          <div className="d-flex align-items-center">
            Are You Sure To Cancel This Placement?
          </div>
        ) : (AccountingCancel && isCancellationRequest) ? (
          <div className="d-flex align-items-center">
            Generating Cancellation Request
          </div>
        ) : (
          <div className="d-flex align-items-center">Enter dates</div>
        )}
      </ModalHeader>

      <ModalBody>
        {!AccountingCancel && (
          <Form>
            <Controller
              name="placementJobTitle"
              control={control}
              defaultValue={placementId?.jobTitle || ""}
              rules={{
                required: {
                  value: true,
                  message: "Job Title is required!",
                },
              }}
              render={({ field }) => (
                <InputField
                  inputtype="text"
                  label="Job Title"
                  inputid="placementJobTitle"
                  isRequired
                  placeholder="Job Title"
                  {...field}
                />
              )}
            />
            <div className="d-flex flex-column">
              <small className="text-danger">
                {errors?.placementJobTitle?.message as any}
              </small>
            </div>

            <Controller
              name="placementEndDate"
              control={control}
              defaultValue=""
              rules={{
                required: {
                  value: true,
                  message: "Temp Assignment End Date is required!",
                },
              }}
              render={({ field }) => (
                <InputField
                  inputtype="date"
                  label="Temp Assignment End Date"
                  inputid="placementEndDate"
                  isRequired
                  placeholder="Temp Assignment End Date"
                  {...field}
                />
              )}
            />
            <div className="d-flex flex-column">
              <small className="text-danger">
                {errors?.placementEndDate?.message as any}
              </small>
            </div>

            <Controller
              name="placementStartDate"
              control={control}
              defaultValue=""
              rules={{
                required: {
                  value: true,
                  message: "DirectHire Start Date is required!",
                },
              }}
              render={({ field }) => (
                <InputField
                  inputtype="date"
                  label="DirectHire Start Date"
                  inputid="placementStartDate"
                  isRequired
                  placeholder="DirectHire Start Date"
                  {...field}
                />
              )}
            />
            <div className="d-flex flex-column">
              <small className="text-danger">
                {errors?.placementStartDate?.message as any}
              </small>
            </div>
          </Form>
        )}

        {AccountingCancel && (
          <div>
            <Label>
              Cancel Reason<span className="text-danger">*</span>
            </Label>
            <Controller
              name="cancelReason"
              control={control}
              defaultValue=""
              rules={{
                required: {
                  value: Boolean(AccountingCancel),
                  message: "Reason is required!",
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  type="textarea"
                  rows="5"
                />
              )}
            />

            <small className="text-danger">
              {errors?.cancelReason?.message as any}
            </small>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <button
          type="button"
          onClick={() => setEndDateModal(false)}
          className="buttonPrimary mr-2"
        >
          Cancel
        </button>
        <CustomButton
          buttonClick={handleSubmit(handleSave)}
          loading={endingPlacementLoading}
          disabled={endingPlacementLoading}
          className="buttonGenericStyle filledButton"
          buttonText="Confirm"
        />

      </ModalFooter>
    </Modal>
  );
};

export default MoldalForDirectHire;
